import { createContext, useContext, useEffect, useState, type ReactNode } from "react"

const THEME_STORAGE_KEY = "barca-theme-v1"
const DARK_MODE_QUERY = "(prefers-color-scheme: dark)"

type ThemeContextValue = {
  theme: "system"
  resolvedTheme: "light" | "dark"
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function getSystemTheme() {
  return window.matchMedia(DARK_MODE_QUERY).matches ? "dark" : "light"
}

function applyTheme(theme: "light" | "dark") {
  const root = document.documentElement
  root.classList.toggle("dark", theme === "dark")
  root.style.colorScheme = theme

  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  themeColor?.setAttribute("content", theme === "dark" ? "#171717" : "#ffffff")
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(getSystemTheme)

  useEffect(() => {
    try {
      window.localStorage.removeItem(THEME_STORAGE_KEY)
    } catch {
      // The system theme still works when browser storage is unavailable.
    }

    const mediaQuery = window.matchMedia(DARK_MODE_QUERY)
    const handleChange = () => setSystemTheme(mediaQuery.matches ? "dark" : "light")

    handleChange()
    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [])

  useEffect(() => {
    applyTheme(systemTheme)
  }, [systemTheme])

  return <ThemeContext.Provider value={{ theme: "system", resolvedTheme: systemTheme }}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) throw new Error("useTheme deve ser usado dentro de ThemeProvider")
  return context
}
